import { useEffect, useMemo } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { App, Form, Input, Radio, Select } from 'antd'
import { GUANGZHOU_TIANHE_REGION_CODES } from '@/features/master-data/const/region-cascader.ts'
import { type RegionSnapshot } from '@/features/master-data/types/region.ts'
import { getRegionCodes, getRegionSnapshot } from '@/features/master-data/utils/region.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { RegionCascader } from './region-cascader.tsx'

export type SiteEditorRecord = {
  id: number
  name: string
  organizationId: number | null
  organization: { id: number; name: string; isDisable: boolean } | null
  operationMode: 'agent' | 'selfOperated'
  provinceCode: string
  provinceName: string
  cityCode: string
  cityName: string
  districtCode: string | null
  districtName: string | null
  detailAddress: string
  note: string | null
}

export type SiteFormValues = {
  name: string
  organizationId?: number
  operationMode: 'agent' | 'selfOperated'
  regionCodes: string[]
  detailAddress: string
  note?: string
}

function getInitialRegionCodes(site?: SiteEditorRecord) {
  if (!site) {
    return [...GUANGZHOU_TIANHE_REGION_CODES]
  }
  return getRegionCodes({
    cityCode: site.cityCode,
    districtCode: site.districtCode ?? undefined,
    provinceCode: site.provinceCode
  })
}

export function SiteEditorModal({
  editing,
  onClose,
  onSaved
}: {
  editing?: SiteEditorRecord | 'new'
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<SiteFormValues>()
  const upsert = useMutation(trpc.site.upsert.mutationOptions())
  const operationMode = Form.useWatch('operationMode', form)
  const organizations = useQuery({
    ...trpc.lookup.organizations.queryOptions({ purpose: 'site.read' }),
    placeholderData: keepPreviousData
  })
  const organizationOptions = (organizations.data?.data ?? []).map((organization) => ({
    label: organization.isDisable ? `${organization.name}（已停用）` : organization.name,
    value: organization.id
  }))
  const initialValues = useMemo<Partial<SiteFormValues>>(
    () =>
      editing && editing !== 'new'
        ? {
            detailAddress: editing.detailAddress,
            name: editing.name,
            note: editing.note ?? undefined,
            organizationId: editing.organizationId ?? undefined,
            operationMode: editing.operationMode,
            regionCodes: getInitialRegionCodes(editing)
          }
        : {
            detailAddress: '',
            name: '',
            note: '',
            organizationId: undefined,
            operationMode: 'selfOperated',
            regionCodes: [...GUANGZHOU_TIANHE_REGION_CODES]
          },
    [editing]
  )

  useEffect(() => {
    if (!editing) {
      return
    }
    form.resetFields()
    form.setFieldsValue(initialValues)
  }, [editing, form, initialValues])

  const save = async (values: SiteFormValues) => {
    try {
      const region: RegionSnapshot = getRegionSnapshot(values.regionCodes)
      await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing?.id,
        data: {
          ...region,
          detailAddress: values.detailAddress.trim(),
          name: values.name.trim(),
          note: values.note?.trim() || undefined,
          operationMode: values.operationMode,
          organizationId: values.operationMode === 'selfOperated' ? values.organizationId : undefined
        }
      })
      void message.success(editing === 'new' ? '场地已创建' : '场地已更新')
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '场地保存失败'))
      return false
    }
  }

  return (
    <ModalForm<SiteFormValues>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存场地' },
        submitButtonProps: { loading: upsert.isPending }
      }}
      title={editing === 'new' ? '新建场地' : `编辑场地 · ${editing?.name ?? ''}`}
      width="40rem"
    >
      <Form.Item label="场地名称" name="name" rules={[{ max: 120, required: true }]}>
        <Input placeholder="请输入物业或场地名称" />
      </Form.Item>
      <Form.Item
        extra={
          operationMode === 'selfOperated'
            ? '房间与后续订单将继承该主体的归属；自营场地必须绑定主体。'
            : '代理场地不绑定经营主体，房间与后续订单按代理模式处理。'
        }
        label="归属主体"
        name="organizationId"
        rules={operationMode === 'selfOperated' ? [{ required: true, message: '自营场地必须选择归属主体' }] : []}
      >
        <Select
          disabled={operationMode !== 'selfOperated'}
          loading={organizations.isLoading}
          options={organizationOptions}
          placeholder={operationMode === 'selfOperated' ? '请选择经营主体' : '代理场地无需绑定'}
          showSearch={{ optionFilterProp: 'label' }}
        />
      </Form.Item>
      <Form.Item label="经营类型" name="operationMode" rules={[{ required: true }]}>
        <Radio.Group
          optionType="button"
          options={[
            { label: '自营', value: 'selfOperated' },
            { label: '代理', value: 'agent' }
          ]}
        />
      </Form.Item>
      <Form.Item label="省市区" name="regionCodes" rules={[{ message: '请选择省、市、区县', required: true }]}>
        <RegionCascader />
      </Form.Item>
      <Form.Item label="详细地址" name="detailAddress" rules={[{ max: 300, required: true }]}>
        <Input placeholder="街道、门牌号、楼栋及房号" />
      </Form.Item>
      <Form.Item label="备注" name="note">
        <Input.TextArea maxLength={2000} placeholder="补充物业或合作信息" rows={4} showCount />
      </Form.Item>
    </ModalForm>
  )
}
